/**
 * 导航 deep link。
 *
 * 坐标一律是 WGS-84 —— 海外市场不做转换。
 * 国内市场需要 GCJ-02 时,由后端 mapConfig.coordSystem 驱动转换,
 * 而不是在这里按坐标猜。见 docs/08-architecture.md §8.3
 */

const PREF_KEY = 'delivery.driver.navApp';

export interface NavOption {
  key: string;
  name: string;
  appUrl?: string;
  webUrl: string;
}

import { isNative, openExternal, platform } from './native';

/** 只有装成 App 时才走自定义 scheme —— 网页里它不可靠 */
function isNativeApp(): boolean {
  return isNative;
}

function isIOS(): boolean {
  return platform === 'ios' || /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export function navOptions(lat: number, lng: number, label: string): NavOption[] {
  const q = encodeURIComponent(label);
  const opts: NavOption[] = [
    {
      key: 'google',
      name: 'Google 地图',
      appUrl: isIOS()
        ? `comgooglemaps://?daddr=${lat},${lng}&directionsmode=driving`
        : `google.navigation:q=${lat},${lng}&mode=d`,
      // 官方跨平台 URL,不需要 API Key。装了 App 会被系统拦截唤起
      webUrl: `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving&dir_action=navigate`,
    },
    {
      key: 'waze',
      name: 'Waze',
      appUrl: `waze://?ll=${lat},${lng}&navigate=yes`,
      webUrl: `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,
    },
  ];

  if (isIOS()) {
    opts.unshift({
      key: 'apple',
      name: '苹果地图',
      appUrl: `maps://?daddr=${lat},${lng}&dirflg=d`,
      webUrl: `https://maps.apple.com/?daddr=${lat},${lng}&dirflg=d&q=${q}`,
    });
  }
  return opts;
}

export const navPreference = {
  get: (): string | null => localStorage.getItem(PREF_KEY),
  set: (key: string): void => localStorage.setItem(PREF_KEY, key),
  clear: (): void => localStorage.removeItem(PREF_KEY),
};

/**
 * 唤起导航。
 *
 * 已记住偏好就直接唤起,不弹选择框 ——
 * 司机一天导航几十次,每次弹窗是酷刑。
 */
export function openNavigation(lat: number, lng: number, label: string): NavOption[] | null {
  const opts = navOptions(lat, lng, label);
  const pref = navPreference.get();
  const chosen = pref ? opts.find((o) => o.key === pref) : null;

  if (chosen) {
    launch(chosen);
    return null;
  }
  // 未设置偏好,返回选项让 UI 弹选择
  return opts;
}

export function launch(opt: NavOption): void {
  /*
   * ★ 网页里**不试自定义 scheme**,直接用官方通用链接。
   *
   *   `comgooglemaps://` / `google.navigation:` 这类地址只有装了 App 的
   *   原生容器里才有意义。在手机浏览器里打开它,行为是各家自己定的:
   *   有的静默失败、有的弹一个错、有的干脆把地址交给系统 ——
   *   而系统可能拿它去开通讯录。司机报的「选了谷歌地图,结果跳到联系人」
   *   就是这条路。
   *
   *   通用链接(https://www.google.com/maps/dir/?api=1...)本来就是
   *   为这件事设计的:装了 App 的手机上,系统会自己把它交给 App。
   */
  if (opt.appUrl && isNativeApp()) {
    const t = setTimeout(() => void openExternal(opt.webUrl), 1200);
    const cancel = () => clearTimeout(t);
    // 页面被切走说明导航 App 起来了,取消回落
    window.addEventListener('pagehide', cancel, { once: true });
    window.addEventListener('blur', cancel, { once: true });
    void openExternal(opt.appUrl);
  } else {
    void openExternal(opt.webUrl);
  }
}
